import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';

import { Divider, Spacer } from 'theSource/components';
import { AppBarStyled } from 'theSource/components/Header/HeaderStyled';
import {
  CaretLeftStyled,
  TitleStyled,
} from 'theSource/components/InternalPage/InternalPageStyled';
import { BenefitDetailsDto } from './types';
import {
  BenefitDetailsTitleStyled,
  BenefitDetailsContainerStyled,
  LearnMoreLinkStyled,
  BackLinkStyled,
  BenefitDetailsDialogStyled,
  BenefitDetailsContentStyled,
} from './BenefitDetailsStyled';

export interface BenefitDetailsProps {
  carrierRequired: Boolean;
  benefitDetails?: BenefitDetailsDto;
  benefitName?: String;
}

export const BenefitDetails = ({
  carrierRequired,
  benefitDetails,
  benefitName,
}: BenefitDetailsProps) => {
  const { t } = useTranslation('pages.benefits');

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const detailsExist = Boolean(
    carrierRequired
      ? benefitDetails?.data?.required_page_content?.page_text &&
          benefitDetails.data.required_page_content.page_text.length > 0
      : benefitDetails?.data?.page_text &&
          benefitDetails.data.page_text.length > 0
  );

  const pageText = detailsExist
    ? carrierRequired
      ? benefitDetails?.data?.required_page_content.page_text
      : benefitDetails?.data?.page_text
    : '';

  const isTextLong = Boolean(
    detailsExist && pageText && pageText.length > 1275
  );

  function createMarkup(truncate: boolean) {
    const markupText = String(pageText);
    return {
      __html: truncate ? markupText.slice(0, 1275) + '... ' : markupText,
    };
  }

  return detailsExist ? (
    <>
      <Spacer height={3} />
      <BenefitDetailsTitleStyled
        data-testid="benefit-details"
        className="headline-3"
      >
        {carrierRequired ? t('carrierBenefitDetails') : t('benefitDetails')}
      </BenefitDetailsTitleStyled>
      <BenefitDetailsContainerStyled>
        <Typography
          data-testid="benefit-details-area"
          dangerouslySetInnerHTML={createMarkup(isTextLong)}
        ></Typography>
        {isTextLong && (
          <LearnMoreLinkStyled
            internal
            value="Learn More"
            id="learn-more"
            onClick={handleOpen}
          />
        )}
      </BenefitDetailsContainerStyled>
      {isTextLong && (
        <BenefitDetailsDialogStyled
          fullScreen
          open={open}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <AppBarStyled position="fixed">
            <Box display="flex" justifyContent="left" height="100%">
              <BackLinkStyled
                internal
                value={t('common:back')}
                underline={false}
                IconComponent={CaretLeftStyled}
                testId="back-button"
                onClick={handleClose}
              />
            </Box>
          </AppBarStyled>
          <BenefitDetailsContentStyled>
            <TitleStyled data-testid="details-dialog-title" variant="h5">
              {carrierRequired
                ? t('carrierBenefitDetails')
                : t('benefitDetails')}
            </TitleStyled>
            <Spacer height={2} />
            <Divider large />
            <Spacer height={5} />
            <TitleStyled
              data-testid="details-dialog-subtitle"
              className="headline-3"
            >
              {benefitName}
            </TitleStyled>
            <Typography
              data-testid="benefit-details-dialog-area"
              dangerouslySetInnerHTML={createMarkup(false)}
            />
          </BenefitDetailsContentStyled>
        </BenefitDetailsDialogStyled>
      )}
    </>
  ) : null;
};
